import React, { useEffect, useState } from "react";

// sass import
import Styles from "./AddPlayersToTeam.module.scss";

// import svg
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DoDisturbAltOutlinedIcon from "@mui/icons-material/DoDisturbAltOutlined";

// importing components
import Cbtn from "../commonbutton/cbtn";
import CModal from "../modal/modal";
import { Avatar, RolePill } from "../tableCells/tableCells";
import { useDispatch, useSelector } from "react-redux";
import {
  pushLastYearSoldPlayers,
  searchFreePlayers,
  selectSearchFreePlayers,
} from "../../store/features/biddingPlayerSlice";

// One row, used for both the selected list and the free list. The old version
// repeated this block three times with inline styles, which is why the header
// and the rows had drifted into different type sizes and column widths.
function PlayerRow({ player, checked, onToggle }) {
  return (
    <li className={`${Styles.row} ${checked ? Styles.rowChecked : ""}`}>
      <label className={Styles.rowLabel}>
        <input
          type="checkbox"
          className={Styles.check}
          checked={checked}
          onChange={() => onToggle(player)}
        />
        <span className={Styles.cellName}>
          <Avatar name={player?.name} image={player?.image} size={30} />
          <span className={Styles.playerName}>{player?.name || "-"}</span>
        </span>
        <span className={Styles.cellRole}>
          <RolePill value={player?.role} />
        </span>
        <span className={Styles.cellNat}>{player?.nationality || "-"}</span>
      </label>
    </li>
  );
}

export const AddPlayersToTeam = ({
  show,
  setShow,
  showError,
  setError,
  franchiseId,
}) => {
  // redux
  let dispatch = useDispatch();
  let searchedFreePlayer = useSelector(selectSearchFreePlayers);

  const [showL, setShowL] = useState();
  const [freePlayers, setFreePlayers] = useState([]);
  const [checkedPlayers, setCheckedPlayers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [recordCount] = useState(50);

  let getData = async () =>
    dispatch(searchFreePlayers({ query: searchQuery, count: recordCount }));

  useEffect(() => {
    let checkedIds = checkedPlayers?.map((p) => p?._id);
    setFreePlayers(
      searchedFreePlayer
        ?.filter((player) => !player?.lastYear?.isSold)
        .filter((p) => !checkedIds?.includes(p?._id))
    );
  }, [searchedFreePlayer]);

  useEffect(() => {
    getData();
  }, [searchQuery]);

  // Moving a player between the two lists rebuilds both arrays rather than
  // splicing them in place; the old version mutated the previous state and
  // only re-rendered because the sibling setState happened to fire.
  const select = (player) => {
    setCheckedPlayers((prev) => [...prev, player]);
    setFreePlayers((prev) => prev.filter((p) => p?._id !== player?._id));
  };

  const deselect = (player) => {
    setCheckedPlayers((prev) => prev.filter((p) => p?._id !== player?._id));
    setFreePlayers((prev) =>
      [...prev, player].sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || ""))
      )
    );
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (checkedPlayers?.length) {
      submitForm();
    } else {
      setError("Select Players to Add");
      showError(true);
    }
  };

  const submitForm = async () => {
    let res = await dispatch(
      pushLastYearSoldPlayers({ franchiseId, freePlayers: checkedPlayers })
    );

    if (res.payload.statusCode === 200) {
      window.location.reload();
    }
    return res;
  };

  const close = setShow ? () => setShow(false) : () => setShowL(false);
  const nothingToShow = !checkedPlayers?.length && !freePlayers?.length;

  return (
    <CModal
      contentClassName={Styles.modalContent}
      show={show ? show : showL}
      onHide={close}
      title=""
      subtitle="Subtitle"
      className={Styles.cmodal}
      centered
    >
      {/* ----------------------heading-------------------------- */}
      <div className={Styles.Header} type="Header">
        <span>
          Free Players
          {/* The count is the only feedback that a tick registered, since the
              selected rows scroll away once the list is long. */}
          {checkedPlayers?.length ? (
            <span className={Styles.count}>{checkedPlayers.length} selected</span>
          ) : null}
        </span>
        <button className={Styles.close} onClick={close} type="button">
          <CloseRoundedIcon style={{ fontSize: "1.4rem" }} />
        </button>
      </div>

      <div className={Styles.Body} type="body">
        <div className={Styles.search}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search players"
          />
        </div>

        <div className={Styles.head}>
          <span className={Styles.headCheck} />
          <span className={Styles.cellName}>Player</span>
          <span className={Styles.cellRole}>Role</span>
          <span className={Styles.cellNat}>Nationality</span>
        </div>

        <ul className={Styles.list}>
          {/* Selected players stay pinned above the rest so a long scroll
              never hides what is about to be added. */}
          {checkedPlayers?.map((player) => (
            <PlayerRow
              key={player?._id}
              player={player}
              checked
              onToggle={deselect}
            />
          ))}
          {freePlayers?.map((player) => (
            <PlayerRow
              key={player?._id}
              player={player}
              checked={false}
              onToggle={select}
            />
          ))}
          {nothingToShow && (
            <li className={Styles.empty}>
              {searchQuery
                ? `No free player matches "${searchQuery}"`
                : "Every player is already on a team"}
            </li>
          )}
        </ul>
      </div>

      {/* ------------------------------FOOTER-------------------------------- */}
      <div type="footer" className={Styles.Footer}>
        <Cbtn
          style={{
            width: "8rem",
            borderRadius: "14px",
            fontSize: ".8rem",
            backgroundColor: "#363b64",
          }}
          onClick={close}
        >
          <DoDisturbAltOutlinedIcon
            fontSize="small"
            sx={{ width: "1rem", height: "1rem", mr: "5px" }}
          />
          <span style={{ margin: "1px" }}>Discard</span>
        </Cbtn>
        <Cbtn
          type="submit"
          onClick={handleSubmit}
          style={{
            width: "8rem",
            fontSize: "0.8rem",
            borderRadius: "14px",
            backgroundColor: "#0040A8",
          }}
        >
          <PersonAddIcon
            fontSize="small"
            sx={{ width: "1.1rem", height: "1.1rem", mr: "5px" }}
          />
          <span style={{ margin: "1px" }}>
            Add{checkedPlayers?.length ? ` ${checkedPlayers.length}` : ""}
          </span>
        </Cbtn>
      </div>
    </CModal>
  );
};
